import React from 'react';
import { SONG_NOTE_STATUS } from '../../../../services/songNotesModel';
import { teacherFirstName } from '../recap/previousRecapsModel';
import './CarryForward.css';

const { FOCUS, LANDED } = SONG_NOTE_STATUS;

/**
 * Pass 3 C3: Carry-forward is OPEN until the lesson has begun (the timer has
 * a start, or the teacher has jotted), then collapsed, at every width (the old
 * phone-collapsed default is gone). The teacher's own toggle wins for the rest
 * of the lesson: `choice` is null until they tap it.
 */
export const carryOpenDefault = ({ choice = null, started = false, jotted = false } = {}) =>
	choice == null ? !(started || jotted) : choice;

/** "Aug 27" from a Firestore Timestamp or a Date; empty when unknown. */
export const noteDateLabel = (note) => {
	const raw = note?.statusChangedAt || note?.createdAt;
	const at = raw?.toDate ? raw.toDate() : raw;
	if (!(at instanceof Date) || Number.isNaN(at.getTime())) return '';
	return at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** "you" for the signed-in teacher, else the first name from the canon roster. */
export const noteTeacherLabel = (note, currentTeacherId) => {
	const uid = note?.authorTeacherId || note?.createdBy || null;
	if (uid && currentTeacherId && uid === currentTeacherId) return 'you';
	return teacherFirstName(uid);
};

/**
 * Carry-forward section (design pass 2, change 4, 2026-09-21): this student's
 * open Song Notes from earlier lessons, every teacher, grouped by song. Replaces
 * BOTH the phone/iPad pill and the rail's own list, so there is one component
 * and one presentation of the data at every width.
 *
 * THE HEADER TOGGLES, AND IT IS NEVER REMOVED. The pill's one-way dismiss hid
 * it for the rest of the lesson with no way back; on an iPad Warm-ups tab that
 * was the only surface, so a teacher could tuck away the previous two
 * teachers' notes by accident and start blind (Grace, 2026-09-20). Show /
 * Collapse is reversible, the count stays readable while collapsed, and the
 * open state belongs to the PAGE so the rail copy and the notes-column copy
 * agree (only one is ever painted, see NotesColumn.css).
 *
 * EACH ITEM NAMES ITS SONG, TIER, TEACHER AND DATE, because on the Warm-ups tab
 * a note from another teacher on another song must read correctly on its own.
 * The tier grammar is the note list's: Focus is delivered and being worked;
 * "Not yet delivered" is private to staff, eye-slash, muted (the teacher
 * named beside it is its author, which may be a colleague). Teacher names
 * come from the canon roster (users/{uid} is self-or-admin readable, so a
 * colleague's uid cannot be resolved through Firestore by an employee).
 *
 * Same data as before: useCarryForward, Song Notes only. Renders nothing when
 * nothing is outstanding; the first-lesson empty state already says so.
 *
 * ACTIONABLE SINCE PASS 3 C4 (2026-09-28). Each item carries one action,
 * right-aligned under its text: **Deliver →** on a note not yet delivered,
 * **Nailed it!** on a Focus note. Both open the Deliver dialog (the ONE gate
 * across the captured→delivered wall) with that choice preselected and the
 * song, section and author shown, so a note from another teacher on another
 * song is confirmed with its context in view. Two taps to Landed, from the
 * Warm-ups tab, without leaving it. `onAct(note, { preselect, songName })`;
 * with no handler the items stay read-only.
 */
const CarryForward = ({
	groups = [],
	totalOpen = 0,
	isLoading = false,
	open = true,
	onToggle,
	currentTeacherId = null,
	onAct = null,
}) => {
	if (!isLoading && totalOpen === 0) return null;

	// The count is its own span so the rail can drop it to a second line
	// (CarryForward.css); the title's text content stays one sentence.
	const summary = isLoading ? (
		'Checking earlier lessons'
	) : (
		<>
			Carry-forward
			<span className="ld-carry__sep"> · </span>
			<span className="ld-carry__count">{`${totalOpen} open Song Note${totalOpen === 1 ? '' : 's'}`}</span>
		</>
	);

	return (
		<section className={`ld-carry${open ? ' is-open' : ''}`} aria-labelledby="ld-carry-title">
			<button
				type="button"
				className="ld-carry__toggle"
				aria-expanded={open}
				aria-controls="ld-carry-body"
				onClick={onToggle}>
				<i className="fa-sharp fa-solid fa-clock-rotate-left" aria-hidden="true" />
				<span id="ld-carry-title" className="ld-carry__title">{summary}</span>
				<span className="ld-carry__action">
					{open ? 'Collapse' : 'Show'}
					<i className={`fa-sharp fa-solid ${open ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" />
				</span>
			</button>

			{open && !isLoading && (
				<ul id="ld-carry-body" className="ld-carry__body">
					{groups.map((group) =>
						[...group.focus, ...group.captured].map((note) => {
							const isFocus = note.status === FOCUS;
							const meta = [
								isFocus ? 'Focus' : 'Not yet delivered',
								noteTeacherLabel(note, currentTeacherId),
								noteDateLabel(note),
							]
								.filter(Boolean)
								.join(' · ');
							return (
								<li key={note.id} className={`ld-carry__item${isFocus ? ' is-focus' : ' is-captured'}`}>
									<i
										className={`fa-sharp fa-solid ${isFocus ? 'fa-bullseye' : 'fa-eye-slash'} ld-carry__status`}
										aria-hidden="true"
									/>
									<span className="ld-carry__text">
										<span className="ld-carry__song">
											<i className="fa-sharp fa-solid fa-music" aria-hidden="true" />
											{group.songName}
										</span>
										{note.text}
										<small className="ld-carry__meta">{meta}</small>
										{onAct && (
											<span className="ld-carry__actions">
												<button
													type="button"
													className={`ld-carry__act${isFocus ? ' ld-carry__act--landed' : ''}`}
													onClick={() =>
														onAct(note, { preselect: isFocus ? LANDED : FOCUS, songName: group.songName })
													}>
													{isFocus ? (
														<>
															<i className="fa-sharp fa-solid fa-circle-check" aria-hidden="true" />
															Nailed it!
														</>
													) : (
														<>
															Deliver
															<i className="fa-sharp fa-solid fa-arrow-right" aria-hidden="true" />
														</>
													)}
												</button>
											</span>
										)}
									</span>
								</li>
							);
						})
					)}
				</ul>
			)}
		</section>
	);
};

export default CarryForward;
